import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Button, Checkbox, EmptyState, IconPhoto, Modal } from '@indipia/ui-core';
import { useEd } from '../store/editor';
import { photoState, setMineOnly } from '../store/photos';

/** 앱 안 확인 창 — 브라우저 confirm() 은 일부 환경에서 막혀 늘 "취소"가 된다 */
// 확인 모달의 확정 버튼은 그 화면의 주 기능이라 primary(D-09 §15.1 v2.26) — danger 는 호출부 호환용으로만 남긴다
export function ConfirmDialog({ title, desc, okLabel = '확인', onClose }: { title: string; desc: string; okLabel?: string; danger?: boolean; onClose: (ok: boolean) => void }) {
  // Esc·포커스 가두기는 Modal 이 한다. Enter 는 확인
  useEffect(() => {
    const key = (e: KeyboardEvent) => { if (e.key === 'Enter' && !(e.target instanceof HTMLButtonElement)) { e.preventDefault(); onClose(true); } };
    document.addEventListener('keydown', key, true);
    return () => document.removeEventListener('keydown', key, true);
  }, [onClose]);
  return (
    <Modal
      open
      title={title}
      onClose={() => onClose(false)}
      footer={<>
        <Button variant="secondary" onClick={() => onClose(false)}>취소</Button>
        <Button variant="primary" onClick={() => onClose(true)}>{okLabel}</Button>
      </>}
    >
      <p className="m-0 text-body-sm leading-relaxed text-text-secondary">{desc}</p>
    </Modal>
  );
}

/** 알림 — 표준 토스트(components.css .toast) */
export function Toast() {
  const e = useEd();
  if (!e.toast.msg) return null;
  return (
    <div className="toast-wrap">
      <div className="toast info" role="status" aria-live="polite">{e.toast.msg}</div>
    </div>
  );
}

/** 현장·사진이 하나도 없을 때 — 인디포토로 보내서 사진을 올리게 한다 */
export function NoPhotos({ compact, onLeave }: { compact?: boolean; onLeave?: () => void }) {
  const navigate = useNavigate();
  const go = (path: string) => { onLeave?.(); navigate(path); };
  return (
    <EmptyState
      className={compact ? "gap-md px-04 py-05" : undefined}
      icon={<IconPhoto />}
      title="아직 인디포토에 올린 현장 사진이 없어요"
      description={'현장을 등록하고 인디포토에 시공 사진을 올리면, 공간 태그대로 템플릿에 들어가고 AI가 글을 써 드려요.'
        + (compact ? '' : ' 사진이 없어도 템플릿 미리보기와 빈 페이지는 쓸 수 있어요.')}
      action={(
        <div className={compact ? "flex w-full flex-col gap-sm" : "flex flex-wrap justify-center gap-sm"}>
          <Button size={compact ? 'sm' : 'md'} onClick={() => go('/agents/photo-organizer/upload?entry=PC')}>사진 올리러 가기</Button>
          <Button size={compact ? 'sm' : 'md'} variant="secondary" onClick={() => go('/sites')}>현장 등록하기</Button>
        </div>
      )}
    />
  );
}

/** "내 현장만 보기" — 끄면(기본) 우리 회사 모든 현장, 켜면 내가 담당자·관리자인 현장만 */
export function MineToggle({ onChange }: { onChange?: () => void }) {
  useEd();
  return (
    <Checkbox
      className="whitespace-nowrap text-text-secondary"
      label="내 현장만 보기"
      title="내가 담당자 또는 관리자로 지정된 현장의 사진만 보여요"
      checked={photoState.mine}
      disabled={photoState.loading}
      onChange={(e) => { void setMineOnly(e.target.checked).then(() => onChange?.()); }}
    />
  );
}
